import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { APP_NAME, AdminApiError, api, tokenStore } from './api';
import { AdminProvider, useAdmin } from './auth';
import { CommandPalette, type Command } from './cmdk';
import { BrandMark, Icon } from './icons';
import { BusinessPage, ModerationLogPage, RolesPage } from './pages/access';
import { AnalyticsPage } from './pages/analytics';
import { PaymentsPage } from './pages/payments';
import { QueuePage } from './pages/queue';
import { SearchPage } from './pages/search';
import { SecurityPage } from './pages/security';
import { SystemPage } from './pages/system';
import { CategoriesPage, LocationsPage, TranslationsPage } from './pages/catalog';
import { BannersPage, PromotionsPage, SettingsPage } from './pages/marketing';
import { Dashboard, ListingsPage, ReportsPage, type DashboardStats } from './pages/moderation';
import { ReviewsPage } from './pages/reviews';
import { AdminsPage, AuditPage, DeletionRequestsPage, UsersPage } from './pages/people';
import { isDark, toggleTheme } from './theme';
import { Btn, ErrorBox, ROLE_RU, useAction } from './ui';

interface NavItem {
  to: string;
  label: string;
  perm: string;
  icon: string;
  group: string;
  element: JSX.Element;
  /** Счётчик на пункте меню (из сводки). */
  badge?: (s: DashboardStats) => number;
}

const NAV: NavItem[] = [
  { group: 'Обзор', to: '/', label: 'Сводка', icon: 'dashboard', perm: 'stats.view', element: <Dashboard /> },
  { group: 'Обзор', to: '/analytics', label: 'Аналитика', icon: 'chart', perm: 'stats.view', element: <AnalyticsPage /> },
  { group: 'Обзор', to: '/system', label: 'Система', icon: 'pulse', perm: 'system.view', element: <SystemPage /> },
  { group: 'Модерация', to: '/queue', label: 'Быстрая модерация', icon: 'bolt', perm: 'listings.moderate', element: <QueuePage />, badge: (s) => s.listings.byStatus.pending ?? 0 },
  { group: 'Модерация', to: '/moderation', label: 'Модерация', icon: 'inbox', perm: 'listings.moderate', element: <ListingsPage key="moderation" defaultStatus="pending" /> },
  { group: 'Модерация', to: '/listings', label: 'Объявления', icon: 'list', perm: 'listings.view', element: <ListingsPage key="all" defaultStatus="active" /> },
  { group: 'Модерация', to: '/reports', label: 'Жалобы', icon: 'flag', perm: 'reports.view', element: <ReportsPage />, badge: (s) => s.openReports },
  { group: 'Модерация', to: '/reviews', label: 'Отзывы', icon: 'star', perm: 'reports.view', element: <ReviewsPage />, badge: (s) => s.reportedReviews ?? 0 },
  { group: 'Модерация', to: '/moderation-log', label: 'Журнал модерации', icon: 'history', perm: 'listings.view', element: <ModerationLogPage /> },
  { group: 'Пользователи', to: '/users', label: 'Пользователи', icon: 'users', perm: 'users.view', element: <UsersPage /> },
  { group: 'Пользователи', to: '/business', label: 'Бизнес-профили', icon: 'briefcase', perm: 'business.manage', element: <BusinessPage />, badge: (s) => s.businessApplications },
  { group: 'Пользователи', to: '/deletion-requests', label: 'Удаление данных', icon: 'trash', perm: 'deletion_requests.process', element: <DeletionRequestsPage />, badge: (s) => s.deletionRequests.pending },
  { group: 'Монетизация', to: '/promotions', label: 'Продвижение', icon: 'rocket', perm: 'promotions.manage', element: <PromotionsPage /> },
  { group: 'Монетизация', to: '/payments', label: 'Платежи', icon: 'wallet', perm: 'payments.view', element: <PaymentsPage /> },
  { group: 'Монетизация', to: '/banners', label: 'Баннеры', icon: 'image', perm: 'banners.manage', element: <BannersPage /> },
  { group: 'Каталог', to: '/categories', label: 'Категории', icon: 'tag', perm: 'categories.manage', element: <CategoriesPage /> },
  { group: 'Каталог', to: '/locations', label: 'География', icon: 'map', perm: 'locations.manage', element: <LocationsPage /> },
  { group: 'Каталог', to: '/translations', label: 'Переводы', icon: 'globe', perm: 'translations.manage', element: <TranslationsPage /> },
  { group: 'Каталог', to: '/search', label: 'Поиск', icon: 'search', perm: 'search.manage', element: <SearchPage /> },
  { group: 'Управление', to: '/settings', label: 'Настройки', icon: 'settings', perm: 'settings.manage', element: <SettingsPage /> },
  { group: 'Управление', to: '/admins', label: 'Администраторы', icon: 'shield', perm: 'admins.manage', element: <AdminsPage /> },
  { group: 'Управление', to: '/roles', label: 'Роли и права', icon: 'key', perm: 'admins.manage', element: <RolesPage /> },
  { group: 'Управление', to: '/audit', label: 'Журнал', icon: 'journal', perm: 'audit.view', element: <AuditPage /> },
];

function Shell() {
  const { me, can, logout } = useAdmin();
  const navigate = useNavigate();
  const loc = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [dark, setDark] = useState(isDark());
  // Политика «2FA обязательна»: до подключения доступен только раздел «Безопасность» (сервер требует то же).
  const mustEnroll = me.twoFactor.required && !me.twoFactor.enabled;
  const items = mustEnroll ? [] : NAV.filter((n) => can(n.perm));
  const stats = useQuery({
    queryKey: ['stats'],
    queryFn: () => api<DashboardStats>('/admin/stats'),
    enabled: !mustEnroll && can('stats.view'),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => setNavOpen(false), [loc.pathname]);

  const current = [...items].sort((a, b) => b.to.length - a.to.length).find((n) => (n.to === '/' ? loc.pathname === '/' : loc.pathname.startsWith(n.to)));
  const groups = useMemo(() => {
    const out: { name: string; items: NavItem[] }[] = [];
    for (const n of items) {
      const g = out.find((x) => x.name === n.group);
      if (g) g.items.push(n);
      else out.push({ name: n.group, items: [n] });
    }
    return out;
  }, [items]);
  const switchTheme = useCallback(() => {
    toggleTheme();
    setDark(isDark());
  }, []);
  const commands: Command[] = useMemo(
    () => [
      ...items.map((n) => ({ id: n.to, label: n.label, hint: n.group, icon: n.icon, run: () => navigate(n.to) })),
      { id: 'security', label: 'Безопасность (2FA)', hint: 'аккаунт', icon: 'shield', run: () => navigate('/security') },
      { id: 'theme', label: dark ? 'Светлая тема' : 'Тёмная тема', hint: 'вид', icon: dark ? 'sun' : 'moon', run: switchTheme },
    ],
    [items, navigate, dark, switchTheme],
  );

  return (
    <div className={`layout ${navOpen ? 'nav-open' : ''}`}>
      {navOpen ? <div className="nav-scrim" onClick={() => setNavOpen(false)} /> : null}
      <aside className="sidebar" aria-label="Разделы">
        <div className="side-brand">
          <BrandMark size={30} />
          <div>
            {APP_NAME}
            <small>Панель управления</small>
          </div>
        </div>
        <nav className="side-nav">
          {groups.map((g) => (
            <div key={g.name} className="side-group">
              <div className="side-group-title">{g.name}</div>
              {g.items.map((n) => {
                const badge = n.badge && stats.data ? n.badge(stats.data) : 0;
                return (
                  <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}>
                    <Icon name={n.icon} />
                    <span>{n.label}</span>
                    {badge > 0 ? <span className="nav-badge" aria-label={`${badge} требуют внимания`}>{badge > 99 ? '99+' : badge}</span> : null}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="side-me">
          <div className="avatar" aria-hidden="true">{me.displayName.slice(0, 1).toUpperCase()}</div>
          <div className="who">
            <b>{me.displayName}</b>
            <span>{ROLE_RU[me.role]}</span>
          </div>
          <button type="button" className="icon-btn" title="Выйти" aria-label="Выйти" onClick={() => void logout()}>
            <Icon name="logout" size={16} />
          </button>
        </div>
      </aside>
      <div className="content">
        <header className="topbar">
          <button type="button" className="icon-btn menu-btn" aria-label="Меню" onClick={() => setNavOpen(true)}>
            <Icon name="menu" />
          </button>
          <div className="crumbs">
            {current ? (
              <>
                {current.group} · <b>{current.label}</b>
              </>
            ) : (
              <b>Безопасность</b>
            )}
          </div>
          <div className="spacer" />
          <button type="button" className="cmdk-trigger" onClick={() => setPaletteOpen(true)} aria-label="Быстрый переход (Ctrl+K)">
            <Icon name="search" size={16} />
            <span>Перейти к разделу или #объявлению</span>
            <kbd>Ctrl K</kbd>
          </button>
          <button type="button" className="icon-btn" onClick={switchTheme} aria-label={dark ? 'Светлая тема' : 'Тёмная тема'} title={dark ? 'Светлая тема' : 'Тёмная тема'}>
            <Icon name={dark ? 'sun' : 'moon'} />
          </button>
          <NavLink to="/security" className={() => 'icon-btn'} aria-label="Безопасность" title={me.twoFactor.enabled ? 'Безопасность' : 'Безопасность: подключите 2FA'}>
            <Icon name="shield" />
          </NavLink>
        </header>
        <main>
          {!me.twoFactor.enabled && !mustEnroll ? (
            <p className="warn" style={{ marginTop: 0 }}>
              Двухфакторный вход не подключён. Подключите его в разделе «Безопасность» — это защищает площадку при утечке пароля.
            </p>
          ) : null}
          <Routes>
            {items.map((n) => (
              <Route key={n.to} path={n.to} element={n.element} />
            ))}
            <Route path="/security" element={<SecurityPage />} />
            <Route path="*" element={<Navigate to={items[0]?.to ?? '/security'} replace />} />
          </Routes>
        </main>
      </div>
      {paletteOpen ? (
        <CommandPalette commands={commands} onClose={() => setPaletteOpen(false)} onListing={(id) => navigate(`/listings?open=${id}`)} />
      ) : null}
    </div>
  );
}

function Login({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const action = useAction();
  return (
    <div className="login">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void action.run(async () => {
            try {
              const r = await api<{ token: string }>('/admin/auth/login', { method: 'POST', json: { email, password, ...(needCode ? { code } : {}) } });
              tokenStore.set(r.token);
              onDone();
            } catch (err) {
              // Пароль верный, нужен второй фактор — показываем поле кода (повторный запрос с паролем и кодом).
              if (err instanceof AdminApiError && err.code === 'TWO_FACTOR_REQUIRED') {
                setNeedCode(true);
                if (!(err.details as { invalid?: boolean } | undefined)?.invalid && !needCode) return;
              }
              throw err;
            }
          });
        }}
      >
        <div className="brand-row">
          <BrandMark size={36} />
          <h1>{APP_NAME}</h1>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>Панель администратора</p>
        <label className="field">
          Email
          <input type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </label>
        <label className="field">
          Пароль
          <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
        </label>
        {needCode ? (
          <label className="field">
            Код из приложения или резервный код
            <input
              inputMode="text"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              required
            />
          </label>
        ) : null}
        <Btn type="submit" busy={action.busy}>
          Войти
        </Btn>
        {action.error ? <ErrorBox error={action.error} /> : null}
      </form>
    </div>
  );
}

export function App() {
  return (
    <AdminProvider login={(done) => <Login onDone={done} />}>
      <Shell />
    </AdminProvider>
  );
}
